'use client';

import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';

interface SectionTransitionProps {
  children: React.ReactNode;
  /** 'circular' = circle wipe from top center, 'horizontal' = left-to-right inset wipe */
  variant?: 'circular' | 'horizontal';
  className?: string;
}

/**
 * Parallax Reveal section transition wrapper.
 * Wraps a section and reveals it with a clip-path animation when scrolled into view.
 */
export default function SectionTransition({
  children,
  variant = 'circular',
  className,
}: SectionTransitionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const clipPathAnimation =
    variant === 'circular'
      ? {
          initial: { clipPath: 'circle(0% at 50% 0%)' },
          animate: inView
            ? { clipPath: 'circle(150% at 50% 0%)' }
            : { clipPath: 'circle(0% at 50% 0%)' },
        }
      : {
          initial: { clipPath: 'inset(0 100% 0 0)' },
          animate: inView
            ? { clipPath: 'inset(0 0% 0 0)' }
            : { clipPath: 'inset(0 100% 0 0)' },
        };

  return (
    <div ref={ref} className="relative overflow-hidden">
      <motion.div
        initial={clipPathAnimation.initial}
        animate={clipPathAnimation.animate}
        transition={{
          duration: 1.1,
          ease: [0.16, 1, 0.3, 1],
          delay: 0.1,
        }}
        className={className}
      >
        {children}
      </motion.div>
      {/* Gold flash on reveal */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: [0, 0.4, 0] } : { opacity: 0 }}
        transition={{ duration: 1.2, ease: 'easeOut', delay: 0.05 }}
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            variant === 'circular'
              ? 'radial-gradient(ellipse 100% 30% at 50% 0%, rgba(210,161,56,0.12) 0%, transparent 70%)'
              : 'linear-gradient(to right, rgba(210,161,56,0.08), transparent 30%)',
        }}
      />
    </div>
  );
}
